import {useTheme, css} from '@mui/material';
import {ConfigObjectType, DlMuiDynamicIcon, DlMuiTypography, GetStylesFunction, TranslateUtils, BadgeWrapper} from '@wallaby-ui/core';
import classNames from 'classnames';
import React from 'react';
import {useIntl} from 'react-intl';
import {usePermissions} from 'src/js/containers';

interface OwnProps {
  isNavigationOpen: boolean;
  isUserMenuOpen: boolean;
  handleOpenUserMenu: () => void;
  user: string;
  config: ConfigObjectType;
  isTooltipInNavigationMenu?: boolean;
}

type Props = OwnProps;

export const NavigationBoxUserSection: React.FC<Props> = (props) => {
  const {isNavigationOpen, isUserMenuOpen, handleOpenUserMenu, user, config, isTooltipInNavigationMenu} = props;
  const theme = useTheme();
  const intl = useIntl();
  const t = (key) => TranslateUtils.translate(key, intl);

  const styles = getStyles({theme, params: {isNavigationOpen}});

  const userIcon = (
    <DlMuiDynamicIcon
      data-name={'navigation-box-user-section--user-icon'}
      css={styles.userIconWrapper}
      // iconTitle={user}
      iconName={'userIcon'}
      inputCss={styles.userIconButton}
      svgIconInputCss={styles.userIcon}
      noButtonStyles
    />
  );

  const {departmentRoles} = usePermissions(config);

  const userIconType = isNavigationOpen ? (
    userIcon
  ) : (
    <DlMuiDynamicIcon
      data-name={'navigation-box-user-section--user-icon'}
      css={styles.userIconWrapper}
      // iconTitle={user}
      iconName={'userIcon'}
      inputCss={styles.userIconButton}
      svgIconInputCss={styles.userIcon}
      onClick={handleOpenUserMenu}
      iconTitle={TranslateUtils.translate('navigation.userMenu', intl)}
      noButtonStyles
      isNavigationOpen={isNavigationOpen}
      isTooltipInNavigationMenu={isTooltipInNavigationMenu}
    />
  );

  return (
    <div
      data-name={'navigation-box-user-section--wrapper'}
      css={styles.userSectionWrapper}
      className={classNames({
        userSectionWrapperActive: isUserMenuOpen,
      })}
    >
      <div data-name={'navigation-box-user-section--content'} css={styles.userSectionContent}>
        <div data-name={'navigation-box-user-section--icon-frame'} css={styles.userIconFrame}>
          {departmentRoles.LIMITED_PERMISSIONS ? (
            <BadgeWrapper
              wrapperCss={styles.userIconFrame}
              xOffset={-3}
              yOffset={-3}
              badgeContent={
                <DlMuiDynamicIcon
                  iconName='badgeExclamationIcon'
                  iconTitle={t('permissions.DEPARTMENT:LIMITED_PERMISSIONS')}
                  useFixedViewBox={12}
                  svgIconInputCss={styles.badgeIcon}
                  noButtonStyles
                />
              }
            >
              {userIcon}
            </BadgeWrapper>
          ) : (
            userIconType
          )}
        </div>
        {isNavigationOpen ? (
          <>
            <DlMuiTypography
              variant={'userName'}
              value={user}
              // title={user}
              data-name={'navigation-box-user-section--user-name'}
              inputCss={styles.userName}
            />
            <DlMuiDynamicIcon
              data-name={'navigation-box-user-section--user-menu-icon'}
              iconName={'menuDots2'}
              iconTitle={t('navigation.userMenu')}
              useFixedViewBox={12}
              onClick={handleOpenUserMenu}
              onKeyPress={handleOpenUserMenu}
              tabIndex={0}
              svgIconInputCss={styles.menuIcon}
              inputCss={styles.openMenuDotsFrame}
              noButtonStyles
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

NavigationBoxUserSection.displayName = 'NavigationBoxUserSection';

const getStyles: GetStylesFunction = (options) => ({
  userSectionWrapper: css`
    height: 40px;
    border-radius: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    background: ${options?.theme?.palette.custom.userSection as string};
    color: ${options?.theme?.palette.custom.white as string};
    &:hover {
      background: ${options?.theme?.palette.custom.userSectionHover as string};
      color: ${options?.theme?.palette.custom.userSectionHoverText as string};
    }
    &.userSectionWrapperActive {
      background: ${options?.theme?.palette.custom.userSectionHover as string};
      color: ${options?.theme?.palette.custom.userSectionHoverText as string};
    }
  `,
  userSectionContent: css`
    height: 100%;
    width: 100%;
    margin: 4px ${options?.params?.isNavigationOpen ? 12 : 0}px 4px ${options?.params?.isNavigationOpen ? 12 : 0}px;
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
    justify-content: ${options?.params?.isNavigationOpen ? 'center' : 'center'};
  `,
  userIconFrame: css`
    width: 24px;
    height: 24px;
    position: relative;
  `,
  userIconWrapper: css({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    color: 'inherit',
  }),
  userIconButton: css`
    padding: 0;
  `,
  userIcon: css`
    width: 12px;
    height: 12px;
  `,
  badgeIcon: css`
    font-size: 12px;
  `,
  userName: css({
    flex: 1,
    color: 'inherit',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '100px',
  }),
  menuIcon: css`
    width: 12px;
    height: 12px;
    cursor: pointer;
    user-select: none;
  `,
  openMenuDotsFrame: css({
    'padding': '0px !important',
    'width': '24px',
    'height': '24px',
    'color': 'currentColor',
    // color: `${options?.theme?.palette.custom.threeDotsColor}`,

    '&:hover': {
      outline: 'none',
      // color: `${options?.theme?.palette.custom.threeDotsColorHover}`,
    },
  }),
});
